import { getDocs, collection } from "firebase/firestore";
import { db } from "../firebase";
import { roundRobin } from "./roundRobin";

/**
 * Round-robin for TEAMS. 
 * If there are more weeks than the number of rounds, we keep cycling from round 1.
 *
 * @param {string} pennantId - The ID of the pennant (to fetch teams).
 * @param {Date[]} allDays - An array of valid match days (e.g. every Sunday in the season).
 * @returns {Promise<Array>} An array of weekly draw objects (one per date).
 */
export default async function generateTeamsDraw(pennantId, allDays) {
  const teamsSnap = await getDocs(collection(db, "Pennants", pennantId, "teams"));
  if (teamsSnap.empty) {
    alert("No teams found. Please generate teams first.");
    return [];
  }

  const allTeams = teamsSnap.docs.map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
  }));

  // Group teams by .section, or fallback to "Unknown"
  const teamsBySection = {};
  allTeams.forEach((team) => {
    let sec = team.section;
    if (!sec) {
      const firstMem = Object.values(team.members || {})[0];
      if (firstMem && firstMem.section) {
        sec = firstMem.section;
      } else {
        sec = "Unknown";
      }
    }
    if (!teamsBySection[sec]) teamsBySection[sec] = [];
    teamsBySection[sec].push(team);
  });

  const finalDraw = [];

  // For each section, generate a round-robin of teams
  Object.entries(teamsBySection).forEach(([sectionKey, sectionTeams]) => {
    if (sectionTeams.length < 2) return; // skip if fewer than 2 teams

    // Build array for roundRobin
    const rrInput = sectionTeams.map((t) => ({
      ...t,
      name: t.name || `Team-${t.id}`,
      section: sectionKey,
    }));

    // Generate the round-robin matchups
    const rounds = roundRobin(rrInput);
    const totalRounds = rounds.length;

    // For each date in allDays, pick a round
    allDays.forEach((day, idx) => {
      const roundIndex = idx % totalRounds;
      const matchups = rounds[roundIndex];

      // Each matchup is { home: TeamDoc, away: TeamDoc }
      const drawMatches = matchups.map(({ home, away }) => {
        // If away is "bye", handle that
        if (!away || away.id === null) {
          return {
            section: sectionKey,
            team1TeamName: home.name,
            team2TeamName: "Bye",
            team1: {
              name: home.name,
              id: home.id,
              section: home.section,
            },
            team2: {
              name: "Bye",
              id: null,
              section: sectionKey,
            },
          };
        }
        // Regular match
        return {
          section: sectionKey,
          team1TeamName: home.name,
          team2TeamName: away.name,
          team1: {
            name: home.name,
            id: home.id,
            section: home.section,
          },
          team2: {
            name: away.name,
            id: away.id,
            section: away.section,
          },
        };
      });

      finalDraw.push({
        section: sectionKey,
        week: idx + 1,
        date: day.toLocaleDateString(),
        matches: drawMatches,
        id: `${pennantId}_${sectionKey}_week-${idx + 1}`,
      });
    });
  });

  return finalDraw;
}
